/**
 * The last line of defence against a white screen.
 *
 * HTTP failures are handled well by every screen (react-query error states),
 * but a *render* throw — usually a 200 response whose shape drifted from the
 * screen's expectations during a mid-deploy API/SPA version skew — unmounts
 * whatever tree it happens inside. Without a boundary that tree is the whole
 * application: blank page, no navigation, no way out but the URL bar.
 *
 * This boundary is mounted around the routed outlet *inside* the shell
 * chrome, so the sidebar and header stay alive and the reader can simply
 * navigate to another screen. It resets itself when the route changes (the
 * `resetKey` prop) and offers a reload for the screen that crashed.
 *
 * A class component because React only exposes `componentDidCatch` /
 * `getDerivedStateFromError` on classes — there is still no hook for this.
 */

import { Component, type ErrorInfo, type JSX, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Changing this (e.g. the current pathname) clears a caught error. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The one place a console line is wanted: this is the crash report.
    // eslint-disable-next-line no-console
    console.error('screen crashed', error, info.componentStack);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      // Navigating away is a legitimate recovery: give the next screen a
      // clean slate instead of trapping every route behind one crash.
      this.setState({ error: null });
    }
  }

  override render(): JSX.Element | ReactNode {
    if (this.state.error === null) {
      return this.props.children;
    }
    return (
      <section className="tvm-card mx-auto w-full max-w-xl p-6" role="alert">
        <p className="text-xs uppercase tracking-wide text-ink-500">Something broke</p>
        <h1 className="mt-1 text-lg font-semibold text-ink-900">
          This screen hit an unexpected error
        </h1>
        <p className="mt-2 text-sm text-ink-700">
          Your data is safe — nothing was saved or changed by the error. Reloading usually clears
          it. If it keeps happening, tell whoever runs Torvanis Money and mention what you were
          doing.
        </p>
        <p className="mt-2 break-words font-mono text-xs text-ink-500">
          {this.state.error.message}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="tvm-button-primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload this screen
          </button>
          <button
            type="button"
            className="tvm-button-secondary"
            onClick={() => {
              this.setState({ error: null });
            }}
          >
            Try again without reloading
          </button>
        </div>
      </section>
    );
  }
}
